/**
 * /try Client Entry Point.
 *
 * WHAT THIS FILE DOES:
 * - Boots the /try client bundle (auth + API + UI).
 * - Creates a WebTryClient instance and exposes it on window for debugging.
 * - Instantiates the DOM controller for the /try surface.
 * - When the create-module tabs flag is on, mounts the tab bar and persists
 *   the active mode in sessionStorage.
 *
 * WHY THIS EXISTS:
 * - The build pipeline targets this file as the /try bundle entry.
 * - Keeping this file tiny prevents the entrypoint from becoming a god module.
 * - Splitting logic across modules makes the bundle easier to maintain.
 *
 * ARCHITECTURE CONTEXT:
 * - The SSR shell (ssr/try.ts) injects Firebase config + languages, and only
 *   injects window.__GIST_TRY_UI_CONFIG__ when the create-module tabs flag is
 *   on. The client treats a missing UI config as "tabs disabled".
 * - The client bundle attaches to pre-rendered DOM nodes.
 * - The extension bridge runs separately via content scripts.
 *
 * CALLED BY:
 * - Browser when /try page loads (script type="module").
 *
 * THIS FILE CALLS:
 * - src/client/try-client.ts (WebTryClient)
 * - src/client/try-ui.ts (TryUIController)
 * - src/client/try-mode-tabs.ts (mountTryModeTabs) when the flag is on
 * - src/client/try-ui-config.ts (resolveTryUiBootstrapConfig)
 *
 * ERROR HANDLING:
 * - Entry point does not catch errors; downstream modules do.
 * - Any uncaught errors surface in the browser console for debugging.
 *
 * GLOBAL CONTRACT:
 * - window.gistTry exposes the client API for console usage.
 * - window.__GIST_TRY_CONFIG__ is injected by SSR (see ssr/try.ts).
 * - window.__GIST_TRY_MEDIA_CONFIG__ is injected by SSR (transcode controls).
 * - window.__GIST_TRY_UI_CONFIG__ is optionally injected by SSR (tabs flag).
 *
 * NON-GOALS:
 * - UI rendering (handled by try-ui.ts).
 * - Auth state management (handled by try-auth.ts).
 * - Network orchestration (handled by try-client.ts).
 *
 * RELATED FILES:
 * - src/client/try-auth.ts (auth + extension bridge)
 * - src/client/try-client.ts (ingest API + polling)
 * - src/client/try-ui.ts (DOM controller)
 * - src/client/try-shared.ts (shared types + constants)
 * - src/client/try-utils.ts (shared helpers)
 *
 * @file src/client/try.ts
 */

import { WebTryClient } from './try-client.js';
import { TryUIController } from './try-ui.js';
import { resolveTryUiBootstrapConfig } from './try-ui-config.js';
import {
  mountTryModeTabs,
  TryModeOrder,
  TryModes,
  type TryMode
} from './try-mode-tabs.js';

const client = new WebTryClient();
window.gistTry = client;

/**
 * sessionStorage key the /try client uses to persist the active create-module
 * mode across reloads inside the same tab. Lives in this entry file because
 * the entry point owns mount-time persistence wiring.
 */
const TRY_MODE_STORAGE_KEY = 'gist.try.mode';

/**
 * Default mode when no persisted mode is present. Mirrors the plan: URL is
 * the dominant /try path today, so it is the safest default.
 */
const DEFAULT_TRY_MODE: TryMode = TryModes.URL;

// Bootstrap the /try client in an async IIFE to support es2020 target.
// Top-level await is not available in es2020, so we wrap in an async function.
// Await auth initialization before mounting UI to prevent speculation
// from firing before the auth controller is ready (A-022).
(async () => {
  await client.init();
  const ui = new TryUIController(client);
  ui.init();

  const uiConfig = resolveTryUiBootstrapConfig(window.__GIST_TRY_UI_CONFIG__);
  if (uiConfig.createModuleTabsEnabled) {
    // The /try bundle loads as a module (deferred), and `client.init()` above
    // awaits async work, so the DOM is parsed by the time we get here.
    mountCreateModuleTabs(ui);
  }
})();

/**
 * Mounts the five-tab create-module signage row when the flag is on.
 *
 * Failure modes are silent: if the SSR slot is missing or sessionStorage
 * throws (rare in private modes), the rest of /try keeps working and falls
 * back to the legacy single-textarea behavior.
 */
function mountCreateModuleTabs(ui: TryUIController): void {
  const slot = document.getElementById('try-mode-tabs');
  if (!slot) return;

  const persisted = readPersistedTryMode();
  const initialMode: TryMode = persisted ?? DEFAULT_TRY_MODE;

  const handle = mountTryModeTabs({
    container: slot,
    defaultMode: initialMode,
    panelId: 'try-input-container',
    onChange: (mode, { userInitiated }) => {
      persistTryMode(mode);
      ui.applyCreatePreset(mode, { focus: userInitiated });
    }
  });

  // Lets the drop handler force File mode when a file is dropped from any
  // preset (programmatic switch — does not steal focus).
  ui.registerCreateModeChanger((mode) => handle.setMode(mode));
}

/**
 * Reads the last active mode from sessionStorage. Returns null when storage
 * is unavailable, the value is missing, or the stored value is not a known
 * mode (defends against stale or hand-edited values).
 */
function readPersistedTryMode(): TryMode | null {
  try {
    const value = window.sessionStorage.getItem(TRY_MODE_STORAGE_KEY);
    if (!value) return null;
    return (TryModeOrder as readonly string[]).includes(value)
      ? (value as TryMode)
      : null;
  } catch {
    return null;
  }
}

/**
 * Best-effort write of the active mode to sessionStorage. Swallows storage
 * errors so a transient SecurityError does not break the tab UX.
 */
function persistTryMode(mode: TryMode): void {
  try {
    window.sessionStorage.setItem(TRY_MODE_STORAGE_KEY, mode);
  } catch {
    // Private mode / storage disabled — silently skip persistence.
  }
}
